/**
 * Photo Lightbox
 * Uses native CSS scroll-snap for swipe navigation — same mechanism as the product zoom dialog.
 */

class PhotoLightbox {
  constructor() {
    this.images = [];
    this.currentIndex = 0;
    this.el = null;
    this.sliderEl = null;
    this.counterEl = null;

    this.collect();
    if (this.images.length === 0) return;
    this.build();
    this.bindEvents();
  }

  collect() {
    document.querySelectorAll('[data-lightbox]').forEach((trigger, index) => {
      const img = trigger.querySelector('img');
      this.images.push({
        src: trigger.dataset.lightboxSrc,
        caption: trigger.dataset.lightboxCaption || '',
        photographer: trigger.dataset.lightboxPhotographer || '',
        location: trigger.dataset.lightboxLocation || '',
        link: trigger.dataset.lightboxLink || '',
        alt: img ? img.alt : ''
      });
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open(index);
      });
    });
  }

  build() {
    this.el = document.createElement('div');
    this.el.id = 'photo-lightbox';
    this.el.setAttribute('role', 'dialog');
    this.el.setAttribute('aria-modal', 'true');
    this.el.hidden = true;

    const slides = this.images.map((data) => {
      const parts = [];
      if (data.photographer) parts.push(data.photographer);
      if (data.location) parts.push(data.location);

      let creditsHtml = '';
      if (parts.length > 0 || data.link) {
        let html = parts.join(' &middot; ');
        if (data.link) {
          const label = data.link.replace(/^https?:\/\//, '');
          html += `${parts.length ? ' &middot; ' : ''}<a href="${data.link}" target="_blank" rel="noopener noreferrer">${label}</a>`;
        }
        creditsHtml = `<p class="phl-credits">${html}</p>`;
      }

      return `
        <div class="phl-slide">
          <div class="phl-content">
            <img class="phl-image" src="${data.src}" alt="${data.caption || data.alt}">
            <div class="phl-meta">
              ${data.caption ? `<p class="phl-caption">${data.caption}</p>` : ''}
              ${creditsHtml}
            </div>
          </div>
        </div>`;
    }).join('');

    const multiple = this.images.length > 1;

    this.el.innerHTML = `
      <button class="phl-close" aria-label="Close lightbox">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
      ${multiple ? `
      <button class="phl-nav phl-prev" aria-label="Previous image">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>
      <button class="phl-nav phl-next" aria-label="Next image">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>
      <div class="phl-counter"></div>` : ''}
      <div class="phl-slider">${slides}</div>`;

    document.body.appendChild(this.el);

    this.sliderEl = this.el.querySelector('.phl-slider');
    this.counterEl = this.el.querySelector('.phl-counter');
  }

  bindEvents() {
    this.el.querySelector('.phl-close').addEventListener('click', () => this.close());

    const prevBtn = this.el.querySelector('.phl-prev');
    const nextBtn = this.el.querySelector('.phl-next');
    if (prevBtn) prevBtn.addEventListener('click', () => this.prev());
    if (nextBtn) nextBtn.addEventListener('click', () => this.next());

    document.addEventListener('keydown', (e) => {
      if (this.el.hidden) return;
      if (e.key === 'Escape')    this.close();
      if (e.key === 'ArrowLeft') this.prev();
      if (e.key === 'ArrowRight') this.next();
    });

    if (this.sliderEl && this.counterEl) {
      this.sliderEl.addEventListener('scroll', () => {
        const index = Math.round(this.sliderEl.scrollLeft / this.sliderEl.clientWidth);
        if (index !== this.currentIndex) {
          this.currentIndex = index;
          this.updateCounter();
        }
      }, { passive: true });
    }
  }

  open(index) {
    this.el.hidden = false;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      this.sliderEl.scrollTo({ left: index * this.sliderEl.clientWidth, behavior: 'instant' });
      this.currentIndex = index;
      this.updateCounter();
    });
    this.el.querySelector('.phl-close').focus();
  }

  close() {
    this.el.hidden = true;
    document.body.style.overflow = '';
  }

  prev() {
    this.scrollToIndex((this.currentIndex - 1 + this.images.length) % this.images.length);
  }

  next() {
    this.scrollToIndex((this.currentIndex + 1) % this.images.length);
  }

  scrollToIndex(index) {
    this.currentIndex = index;
    this.sliderEl.scrollTo({ left: index * this.sliderEl.clientWidth, behavior: 'smooth' });
    this.updateCounter();
  }

  updateCounter() {
    if (this.counterEl) {
      this.counterEl.textContent = `${this.currentIndex + 1} / ${this.images.length}`;
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new PhotoLightbox());
} else {
  new PhotoLightbox();
}
